export default defineNuxtPlugin(() => {
    if (!process.client) return

    const isTelegramWebView = () => {
        const ua = navigator.userAgent

        // Расширенная проверка Telegram WebView
        const checks = {
            userAgentTelegram: ua.includes('Telegram'),
            userAgentWebView: ua.includes('WebView'),
            userAgentTgWebApp: ua.includes('TelegramWebApp'),
            urlParam: window.location.search.includes('tgWebAppPlatform'),
            telegramAPI: !!(window as any).Telegram?.WebApp,
            telegramWebview: !!(window as any).TelegramWebview,
            // Дополнительные проверки для мобильных браузеров Telegram
            isMobileTelegram: ua.includes('Mobile') && (ua.includes('Telegram') || ua.includes('tg_owt')),
            hasInitData: window.location.hash.includes('tgWebAppData') || window.location.search.includes('tgWebAppData'),
            // Проверка через window.parent (если в iframe)
            inIframe: window !== window.parent
        }

        const isTelegram = Object.values(checks).some(Boolean)

        // Подробное логирование
        // console.log('[TG-Monitor] Environment check:', {
        //     userAgent: ua,
        //     checks,
        //     isTelegram,
        //     url: window.location.href,
        //     platform: (window as any).Telegram?.WebApp?.platform || 'unknown',
        //     referrer: document.referrer
        // })

        return isTelegram
    }

    // Для отладки - всегда инициализируем в dev режиме или если есть параметр debug
    const forceDebug = window.location.search.includes('tg_debug=1') ||
        window.location.search.includes('debug=true') ||
        window.location.search.includes('force_tg=true') ||
        process.dev

    const isTelegramDetected = isTelegramWebView()
    const shouldInit = isTelegramDetected || forceDebug

    // console.log('[TG-Monitor] Init decision:', { shouldInit, forceDebug, isTelegramDetected })

    if (!shouldInit) return

    // Конфиг для production
    const config = {
        enabled: true, // Включено всегда
        maxLogs: 100,
        logLevel: 'error', // В production логируем только ошибки
        autoExport: false
    }

    const logger = {
        storageKey: 'tg_monitor_logs',

        save: (level: string, message: string, data?: any) => {
            if (!config.enabled) return

            try {
                const logs = JSON.parse(sessionStorage.getItem(logger.storageKey) || '[]')
                const logEntry = {
                    timestamp: new Date().toISOString(),
                    level,
                    message: message.substring(0, 500), // Ограничиваем длину
                    data: data ? logger.sanitizeData(data) : null,
                    url: window.location.pathname, // Только path, без полного URL
                    memory: 'memory' in performance ? (performance as any).usedJSHeapSize : null
                }

                logs.push(logEntry)

                if (logs.length > config.maxLogs) {
                    logs.splice(0, logs.length - config.maxLogs)
                }

                sessionStorage.setItem(logger.storageKey, JSON.stringify(logs))

                // В production не дублируем в console, только сохраняем в sessionStorage
            } catch (e) {
                // Молча игнорируем ошибки логирования
            }
        },

        sanitizeData: (data: any) => {
            if (typeof data === 'object') {
                const sanitized = { ...data }
                delete sanitized.token
                delete sanitized.password
                delete sanitized.email
                delete sanitized.authorization
                return sanitized
            }
            return data
        },

        getLogs: () => {
            try {
                return JSON.parse(sessionStorage.getItem(logger.storageKey) || '[]')
            } catch {
                return []
            }
        },

        exportLogs: () => {
            try {
                const logs = logger.getLogs()
                const dataStr = JSON.stringify(logs, null, 2)
                const blob = new Blob([dataStr], { type: 'application/json' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `tg-logs-${Date.now()}.json`
                a.style.display = 'none'
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                URL.revokeObjectURL(url)
            } catch (e) {
                console.error('Export failed:', e)
            }
        },

        clearLogs: () => {
            try {
                sessionStorage.removeItem(logger.storageKey)
            } catch (e) {
                // Ignore
            }
        }
    }

    const log = {
        debug: (message: string, data?: any) => {
            logger.save('debug', message, data)
        },
        warn: (message: string, data?: any) => {
            logger.save('warn', message, data)
        },
        error: (message: string, data?: any) => {
            logger.save('error', message, data)
        }
    }

    // Мониторинг для production
    const startMonitoring = () => {
        log.debug('Production monitoring started')

        const startTime = Date.now()
        let errorCount = 0
        let checkCount = 0

        // Проверяем Telegram WebApp API
        const checkTelegramAPI = () => {
            const tg = (window as any).Telegram?.WebApp
            if (tg) {
                log.debug('Telegram WebApp API available', {
                    version: tg.version,
                    platform: tg.platform,
                    colorScheme: tg.colorScheme,
                    themeParams: tg.themeParams,
                    isExpanded: tg.isExpanded,
                    viewportHeight: tg.viewportHeight,
                    viewportStableHeight: tg.viewportStableHeight
                })
            } else {
                log.warn('Telegram WebApp API not available')
            }
        }

        const healthCheck = () => {
            checkCount++

            // В production проверяем только критичные вещи
            if (document.visibilityState === 'visible') {
                const rootElement = document.querySelector('#__nuxt')
                const bodyClasses = document.body.className
                const bodyStyles = getComputedStyle(document.body)

                // Проверяем основные проблемы
                const issues = []

                if (!rootElement || rootElement.children.length === 0) {
                    issues.push('root_element_empty')
                    errorCount++
                }

                if (bodyStyles.backgroundColor === 'rgb(0, 0, 0)' || bodyStyles.color === 'rgb(0, 0, 0)') {
                    issues.push('black_screen_detected')
                }

                if (document.styleSheets.length === 0) {
                    issues.push('no_stylesheets')
                }

                if (issues.length > 0) {
                    log.error('Health check issues detected', {
                        issues,
                        checkCount,
                        errorCount,
                        rootExists: !!rootElement,
                        rootChildren: rootElement?.children.length,
                        bodyClasses,
                        bodyBgColor: bodyStyles.backgroundColor,
                        bodyColor: bodyStyles.color,
                        stylesheets: document.styleSheets.length,
                        scripts: document.scripts.length
                    })

                    // После 3 ошибок подряд - перезагрузка
                    if (errorCount >= 3) {
                        log.error('Force reload after multiple errors')
                        setTimeout(() => window.location.reload(), 2000)
                    }
                } else {
                    errorCount = 0 // Сбрасываем счетчик ошибок
                }

                // Логируем только каждую 10-ю проверку в production
                if (checkCount % 10 === 0) {
                    log.debug('Periodic health check', {
                        checkCount,
                        sessionDuration: Date.now() - startTime,
                        memoryUsage: (performance as any).usedJSHeapSize
                    })
                }
            }
        }

        // Проверяем Telegram API при запуске
        checkTelegramAPI()

        // В production проверяем реже - каждые 60 секунд
        const interval = setInterval(healthCheck, 60000)

        // Перехват ошибок
        const originalConsoleError = console.error
        console.error = (...args) => {
            // Игнорируем собственные логи чтобы избежать бесконечного цикла
            const message = args[0]?.toString() || ''
            if (!message.includes('[TG-Monitor]')) {
                log.error('Console error', { args: args.map(arg => arg.toString()) })
            }
            originalConsoleError.apply(console, args)
        }

        window.addEventListener('error', (event) => {
            log.error('Window error', {
                message: event.message,
                filename: event.filename,
                lineno: event.lineno
            })
        })

        window.addEventListener('unhandledrejection', (event) => {
            log.error('Unhandled rejection', {
                reason: event.reason?.message || String(event.reason)
            })
        })

        // Мониторинг загрузки ресурсов
        const originalFetch = window.fetch
        window.fetch = async (...args) => {
            const start = Date.now()
            try {
                const response = await originalFetch(...args)
                const duration = Date.now() - start

                if (!response.ok) {
                    log.error('Fetch failed', {
                        url: args[0],
                        status: response.status,
                        statusText: response.statusText,
                        duration
                    })
                }

                return response
            } catch (error) {
                log.error('Fetch error', {
                    url: args[0],
                    error: error instanceof Error ? error.message : String(error),
                    duration: Date.now() - start
                })
                throw error
            }
        }

        // Проверяем загрузку CSS
        const checkCSSLoaded = () => {
            const stylesheets = Array.from(document.styleSheets)
            const loadedStyles = stylesheets.filter(sheet => {
                try {
                    return sheet.cssRules && sheet.cssRules.length > 0
                } catch {
                    return false
                }
            })

            if (stylesheets.length === 0) {
                log.error('No stylesheets found')
            } else if (loadedStyles.length === 0) {
                log.error('Stylesheets not loaded properly', {
                    total: stylesheets.length,
                    loaded: loadedStyles.length
                })
            }
        }

        // Проверяем CSS через 3 секунды после загрузки
        setTimeout(checkCSSLoaded, 3000)

        return () => {
            clearInterval(interval)
            console.error = originalConsoleError // Восстанавливаем original
        }
    }

    let stopMonitoring: () => void

    // Запускаем с задержкой, чтобы не мешать первоначальной загрузке
    setTimeout(() => {
        stopMonitoring = startMonitoring()
    }, 5000)

    // Делаем API доступным глобально (только для отладки)
    if (typeof window !== 'undefined') {
        (window as any).__TG_MONITOR__ = {
            getLogs: () => logger.getLogs(),
            exportLogs: () => logger.exportLogs(),
            clearLogs: () => logger.clearLogs(),
            sendLogsToTelegram: () => sendLogsToTelegram(),
            version: '1.0'
        }
        // console.log('[TG-Monitor] Global API initialized:', (window as any).__TG_MONITOR__)
    }

    // Отправка логов в Telegram через WebApp API
    const sendLogsToTelegram = () => {
        try {
            const logs = logger.getLogs()
            const tg = (window as any).Telegram?.WebApp

            if (tg && logs.length > 0) {
                const logsText = logs.map((log: any) =>
                    `[${log.level.toUpperCase()}] ${log.message}\n${log.data ? JSON.stringify(log.data, null, 2) : ''}`
                ).join('\n\n')

                // Отправляем логи через Telegram WebApp API
                tg.showPopup({
                    title: 'Debug Logs',
                    message: logsText.substring(0, 200) + (logsText.length > 200 ? '...' : ''),
                    buttons: [
                        { id: 'copy', type: 'default', text: 'Копировать все' },
                        { id: 'close', type: 'cancel', text: 'Закрыть' }
                    ]
                }, (buttonId: string) => {
                    if (buttonId === 'copy') {
                        navigator.clipboard?.writeText(logsText)
                    }
                })
            }
        } catch (e) {
            log.error('Failed to send logs to Telegram', { error: e instanceof Error ? e.message : String(e) })
        }
    }

    return {
        provide: {
            telegramMonitor: {
                log,
                // Минимальный API для компонентов
                recordError: (message: string, data?: any) => log.error(message, data)
            }
        }
    }
})